import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import {
  getMonitoringEnvironment,
  getHealthCheckHistory,
  getUptimeTrends,
} from '../../../services/cloudopsApi';
import ResponseTimeChart from '../components/ResponseTimeChart';
import HealthCheckHistoryTable from '../components/HealthCheckHistoryTable';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface MonitoringEnvDetail {
  name: string;
  current_status?: string;
  environment_type?: string;
  endpoint_url: string;
  uptime_percentage?: number;
  check_interval_minutes?: number;
  timeout_seconds?: number;
  expected_status_code?: number;
}

interface UptimeTrendItem {
  environment_id: string;
  date: string;
  uptime_percentage: number;
}

interface UptimeChartPoint {
  date: string;
  uptime: number;
}

interface UptimeTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number | null }>;
  label?: string;
}

const UptimeTooltip = ({ active, payload, label }: UptimeTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">{label}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300">
        Uptime: {payload[0].value == null ? 'N/A' : `${payload[0].value.toFixed(2)}%`}
      </p>
    </div>
  );
};

export default function MonitoringEnvironmentDetailPage() {
  const { environmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId } = useClient();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<MonitoringEnvDetail | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [healthChecks, setHealthChecks] = useState<any[]>([]);
  const [uptimeTrends, setUptimeTrends] = useState<UptimeTrendItem[]>([]);
  const [days, setDays] = useState<number>(7);

  const loadData = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const clientId = selectedClientId || user?.client_id || undefined;

      const envId = environmentId!;
      const [envData, checksData, trendsData] = await Promise.all([
        getMonitoringEnvironment(envId),
        getHealthCheckHistory(envId, days, 200),
        getUptimeTrends({ clientId, days }),
      ]);

      setEnvironment(envData);
      setHealthChecks(checksData);
      setUptimeTrends(trendsData);
      if (silent) setError(null);
    } catch (err: unknown) {
      console.error('Error loading environment detail:', err);
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load environment data');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [environmentId, days, selectedClientId, user?.client_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => {
      loadData(true);
    }, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
            Error Loading Environment
          </h3>
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => loadData()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!environment) return null;

  const status = environment.current_status?.toLowerCase() || 'unknown';

  const statusBadgeClasses: Record<string, string> = {
    healthy: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    down: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
  };

  const typeBadgeClasses: Record<string, string> = {
    production: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
    staging: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    development: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    qa: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    uat: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
  };

  // Stats calculations
  const totalChecks = healthChecks.length;
  const successChecks = healthChecks.filter((c) => c.is_healthy).length;
  const failureChecks = totalChecks - successChecks;
  const avgResponseTime =
    totalChecks > 0
      ? healthChecks.reduce((sum, c) => sum + (c.response_time_ms || 0), 0) / totalChecks
      : 0;

  // Filter uptime trends for this environment
  const envUptimeTrends = uptimeTrends.filter(
    (t) => String(t.environment_id) === String(environmentId)
  );

  // Build uptime chart data
  const uptimeChartData: UptimeChartPoint[] = envUptimeTrends
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((item) => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      uptime: item.uptime_percentage,
    }));

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/cloudops/endpoints')}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1 mb-4"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Monitoring
        </button>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {environment.name}
          </h1>
          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusBadgeClasses[status] || statusBadgeClasses.unknown}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          <span className={`px-2 py-1 text-xs font-semibold rounded ${typeBadgeClasses[environment.environment_type || 'production'] || typeBadgeClasses.production}`}>
            {environment.environment_type?.toUpperCase()}
          </span>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 break-all">
          {environment.endpoint_url}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 transition-colors">
          <p className="text-sm text-gray-500 dark:text-gray-400">Uptime</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {environment.uptime_percentage == null ? 'N/A' : `${environment.uptime_percentage.toFixed(2)}%`}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 transition-colors">
          <p className="text-sm text-gray-500 dark:text-gray-400">Avg Response Time</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {avgResponseTime > 0 ? `${avgResponseTime.toFixed(0)}ms` : 'N/A'}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 transition-colors">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Checks</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {totalChecks}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-5 transition-colors">
          <p className="text-sm text-gray-500 dark:text-gray-400">Success / Failure</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            <span className="text-green-600 dark:text-green-400">{successChecks}</span>
            {' / '}
            <span className="text-red-600 dark:text-red-400">{failureChecks}</span>
          </p>
        </div>
      </div>

      {/* Environment Info */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-8 transition-colors">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Environment Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Name</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{environment.name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Endpoint URL</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white break-all">{environment.endpoint_url}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Type</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{environment.environment_type}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Check Interval</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {environment.check_interval_minutes ? `${environment.check_interval_minutes} min` : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Timeout</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {environment.timeout_seconds ? `${environment.timeout_seconds}s` : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Expected Status Code</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {environment.expected_status_code ?? 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Days Filter */}
      <div className="flex items-center gap-2 mb-6">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-colors ${
              days === d
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Uptime Timeline Chart */}
      {uptimeChartData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-8 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Uptime Timeline
          </h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={uptimeChartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                <XAxis
                  dataKey="date"
                  className="text-gray-600 dark:text-gray-400"
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 100]}
                  className="text-gray-600 dark:text-gray-400"
                  tick={{ fontSize: 12 }}
                  label={{ value: 'Uptime %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<UptimeTooltip />} />
                <Line
                  type="monotone"
                  dataKey="uptime"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Response Time Chart */}
      <div className="mb-8">
        <ResponseTimeChart healthChecks={healthChecks} />
      </div>

      {/* Health Check History */}
      <div>
        <HealthCheckHistoryTable healthChecks={healthChecks} days={days} />
      </div>
    </div>
  );
}
